import React, { ReactNode } from 'react';
import { FormProvider, SubmitHandler, UseFormProps, UseFormReturn } from 'react-hook-form';

import { datadogRum } from '@datadog/browser-rum';
import { redactPIIForFormData, trackEvent } from '@helios/frontend-core';

export interface Props<T extends Record<string, unknown>> extends UseFormProps {
  children: ReactNode;
  onSubmit: SubmitHandler<T>;
  methods: UseFormReturn<T>;
  trackingId: string;
  className?: string;
  id?: string;
  formRef?: React.RefObject<HTMLFormElement>;
}

const isFileList = (value: unknown) =>
  typeof value === 'object' && value !== null && 'length' in value && 'item' in value;

const filterFiles = (formData: Record<string, unknown>) => {
  const newObject = { ...formData };
  try {
    Object.entries(newObject).forEach(([key, value]) => {
      if (isFileList(value)) {
        newObject[key] = Array.from(value as FileList).map((item) => item.name || item);
      }
    });
  } catch (e) {
    datadogRum.addError('unexpected error in filterFiles', e);
  }
  return newObject;
};

const sanitizeFormDataForTracking = (formData: Record<string, unknown>) =>
  filterFiles(redactPIIForFormData(formData));

function Form<T extends Record<string, unknown>>(props: Props<T>): React.ReactElement {
  const { children, onSubmit, methods, className, trackingId, id, formRef } = props;

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        id={id}
        noValidate
        className={className}
        onSubmit={(event: React.SyntheticEvent) => {
          trackEvent('form submit', {
            trackingId,
            data: sanitizeFormDataForTracking(methods.getValues()),
            page: window.location.href,
          });
          return methods.handleSubmit(onSubmit)(event);
        }}
        data-tracking-id={trackingId}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export default Form;
